import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { OUTPUT_MODE_OPTIONS } from '../../constants/outputModes';

/**
 * Compact output-style control for dashboard AI cards.
 */
const OutputModeSwitcher = ({
  value,
  onChange,
  disabled = false,
  loading = false,
  size = 'small',
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const current = OUTPUT_MODE_OPTIONS.find((o) => o.value === value);

  const handleOpen = (e) => {
    e.stopPropagation();
    if (!disabled && !loading) setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (mode) => (e) => {
    e.stopPropagation();
    handleClose();
    if (mode !== value && onChange) onChange(mode);
  };

  return (
    <>
      <Tooltip
        title={
          loading
            ? 'Regenerating insights…'
            : `Output style: ${current?.label || 'Elaborate'}. Click to change.`
        }
      >
        <span>
          <IconButton
            size={size}
            onClick={handleOpen}
            disabled={disabled || loading}
            aria-label="Change output style"
            sx={{
              ml: 'auto',
              color: 'rgb(64, 121, 141)',
              p: 0.25,
              '&:hover': { bgcolor: 'rgba(64, 121, 141, 0.08)' },
            }}
          >
            {loading ? (
              <CircularProgress size={16} sx={{ color: 'rgb(64, 121, 141)' }} />
            ) : (
              <TuneOutlinedIcon sx={{ fontSize: '1.1rem' }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 200, mt: 0.5 } } }}
      >
        {OUTPUT_MODE_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} selected={opt.value === value} onClick={handleSelect(opt.value)}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              {opt.value === value ? <CheckIcon fontSize="small" color="primary" /> : null}
            </ListItemIcon>
            <ListItemText
              primary={opt.label}
              secondary={opt.description}
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.7rem', sx: { whiteSpace: 'normal' } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default OutputModeSwitcher;
